type PillSelectProps = {
  values: string | string[];
  onChange: (xs: string[]) => void;
  options: Array<{ name: string; value: string }>;
  disabled?: boolean;
  label: string;
  multi?: boolean;
  limit?: number;
  Pill?: typeof DefaultPill;
  gap?: number;
  required?: boolean;
  invalid?: unknown;
};
export default function PillSelect({
  values,
  onChange,
  options,
  disabled = false,
  label,
  invalid,
  required = false,
  multi = false,
  limit = undefined,
  Pill = DefaultPill,
  gap = 2,
}: PillSelectProps) {
  values = Array.isArray(values) ? values : [values];
  const allOptions = options.map((o) => o.value);
  values = values.filter((x) => allOptions.includes(x));

  function toggleSelect(values: string[], option: string): string[] {
    if (disabled) {
      return;
    }

    if (values.includes(option)) {
      return values.filter((x) => x != option);
    } else {
      if (!multi) {
        return [option];
      }

      if (limit && values.length >= limit) {
        return values;
      }

      return values.concat(option);
    }
  }

  return (
    <div>
      <div>
        <label className="mb-2">
          <strong
            className="font-semibold"
            style={{ borderBottom: invalid ? '1px solid red' : 'none' }}
          >
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </strong>
        </label>
      </div>
      <div className={`flex flex-wrap gap-${gap} max-w-4xl`}>
        {options.map((o) => (
          <Pill
            key={o.value}
            o={o}
            isActive={values.includes(o.value)}
            onClick={() => onChange(toggleSelect(values as string[], o.value))}
          />
        ))}
      </div>
      {limit && (
        <div className="mt-1">
          <small>Selects remaining: {limit - values.length}</small>
        </div>
      )}
    </div>
  );
}

export type PillProps = {
  o: { name: string; value: string };
  isActive: boolean;
  onClick: () => void;
};
function DefaultPill({ o, isActive, onClick }: PillProps) {
  return (
    <div
      className="rounded-lg px-3 flex items-center whitespace-nowrap"
      key={o.value}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        height: '40px',
        color: isActive ? 'white' : 'black',
        background: isActive ? '#626BE7' : '#f2f4f6',
      }}
    >
      {o.name}
    </div>
  );
}

export function WorkoutDurationPill({ o, isActive, onClick }: PillProps) {
  return (
    <div
      className="rounded-lg px-3 flex items-center whitespace-nowrap"
      key={o.value}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        height: '40px',
        color: 'white',
        background: `rgba(255, 255, 255, ${isActive ? '0' : '0.1'})`,
        border: isActive
          ? '2px solid white'
          : '2px solid rgba(255, 255, 255, 0)',
      }}
    >
      {o.name}
    </div>
  );
}

export function SquarePill({
  o,
  isActive,
  onClick,
  color = 'black',
  fontWeight = 'bold',
}: PillProps & {
  color?: string;
  fontWeight?: string;
}) {
  return (
    <div
      className="flex px-3 items-center justify-center whitespace-nowrap"
      key={o.value}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        height: '40px',
        color: isActive ? '#626BE7' : color,
        border: '2px solid',
        borderColor: isActive ? '#626BE7' : '#f2f4f6',
        fontWeight,
        width: '120px',
      }}
    >
      {o.name}
    </div>
  );
}
